import React from "react";
import ReactDOM from "react-dom";
import FindDealerStore from "../../stores/FindDealerStore";
import { Breakpoint } from "../../utils/Breakpoints";
import { observer, inject } from "mobx-react";
import {
  RetailerInfo,
  FilterDepartmentsDropDown,
  FilterDepartmentsList,
} from ".";
import { Dealer, DealerDetails, SuburbModel } from "../../models";
import Loader from "../Loader";
import { ContentImagePath } from "../../utils/ContentMediaPath";
import clusterStyles from "../../utils/ClusterStyles";
import classNames from "classnames";
import throttle from "lodash/throttle";
import { mapStyles } from "./mapStyles";
import TagManager from "react-gtm-module";
import PostcodeField from "../Postcode/PostcodeField";

interface Props {
  findDealerStore?: FindDealerStore;
  showRating?: boolean;
}

interface State {
  breakpoint: string;
  isVisible: boolean;
  suburb: SuburbModel;
  preSelectedSuburb: SuburbModel;
}

declare var MarkerClusterer: any;
let mapLoads: number = 0;
let mapUpdates: number = 0;
let markerRedraws: number = 0;
const pinIcon = ContentImagePath("map-pin-blue.svg");
const pinActiveIcon = ContentImagePath("map-pin-green.svg");
let prevStoreState: FindDealerStore = null;
const dealerRatingsCache = {}; //Local Cache

const log: boolean = document.location.href.indexOf("?log") !== -1;

@inject("findDealerStore")
@observer
class RetailersPopupMap extends React.Component<Props, State> {
  map: google.maps.Map;
  marker: google.maps.Marker;
  infowindow: google.maps.InfoWindow = new google.maps.InfoWindow({
    maxWidth: 200,
  });
  markers: google.maps.Marker[] = [];
  bounds: google.maps.LatLngBounds;
  markerClusterer: any = null;
  postcodeField: PostcodeField = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      breakpoint: Breakpoint(),
      isVisible: false,
      suburb: null,
      preSelectedSuburb: null,
    };
    this.loadMap = this.loadMap.bind(this);
    this.createMarkers = this.createMarkers.bind(this);
    this.showAll = this.showAll.bind(this);
    this.fetchRating = this.fetchRating.bind(this);
    this.resetMarkersIcon = this.resetMarkersIcon.bind(this);

    window.addEventListener(
      "resize",
      throttle(() => {
        this.setState({
          breakpoint: Breakpoint(),
        });
      }, 500)
    );

    window.addEventListener("megaNavDealerChanged", (e: any) => {4
      const { fetchDealers, dealers, suburb } = this.props.findDealerStore;
      suburb.postcode = e.detail.postCode;
      suburb.name = e.detail.suburb;
      fetchDealers(true);
      const userDealer = dealers.find(
        (dealer) => dealer.crmDealerId === e.detail.crmDealerId
      );
      this.props.findDealerStore.selectDealer(userDealer);
    });

    window.onpopstate = () => {
      if (!this.map) {
        const selectedDealer = this.props.findDealerStore.selectedDealer;
        this.loadMap();
        this.createMarkers();

        if (selectedDealer) {
          mapUpdates++;
          this.resetMarkersIcon(selectedDealer);
          this.map.setZoom(15);
          this.fetchRating(
            selectedDealer.dealerDetails && selectedDealer.dealerDetails[0]
              ? selectedDealer.dealerDetails[0].googlePlaceId
              : undefined
          );
        }
      }
      log &&
        console.log(
          "Loads:" + mapLoads,
          "Updates:" + mapUpdates,
          "MarkerRedraws:" + markerRedraws
        );
    };

    window.addEventListener("modelSelected", (e: any) => {
      const ready = () => {
        const { fetchDealers } = this.props.findDealerStore;
        fetchDealers(true);
      };

      if (document.readyState === "complete") {
        ready();
      } else {
        window.addEventListener("load", () => {
          log && console.log("Delay location restore until load.");
          ready();
        });
      }
    });
  }

  componentWillUpdate(nextProps: Props, nextState: State) {
    const { dealers, selectedDealer, dealerSelects, selectDealer } =
      nextProps.findDealerStore;
    let didUpdate: boolean = false;
    const mapWasLoaded = !!this.map;

    if (!mapWasLoaded) {
      didUpdate = true;
      this.loadMap();
    }

    if (nextState.breakpoint !== this.state.breakpoint) {
      didUpdate = true;
      this.mapFixOnBreakpointChange(nextState.breakpoint);
    }

    if (dealers.length < 1) return;

    if (
      selectedDealer &&
      !dealers.some((x) => x.crmDealerId === selectedDealer.crmDealerId)
    ) {
      selectDealer(dealers[0]);
      return;
    }

    // if (prevStoreState.dealers.length !== dealers.length) {
    didUpdate = true;
    this.createMarkers(nextProps);
    // }

    if (
      (prevStoreState.selectedDealer !== selectedDealer && selectedDealer) ||
      (!mapWasLoaded && selectedDealer)
    ) {
      didUpdate = true;
      this.resetMarkersIcon(selectedDealer);
      this.map.setZoom(15);
      this.fetchRating(
        selectedDealer.dealerDetails && selectedDealer.dealerDetails[0]
          ? selectedDealer.dealerDetails[0].googlePlaceId
          : undefined
      );

      if (this.markers.length) {
        this.markers.forEach((marker) => {
          if (marker.get("dealer").name === selectedDealer.name) {
            this.map.panTo(marker.getPosition());
            marker.setIcon(this.createMarkerIcon(true));
          }
        });
      }

      if (dealerSelects === 1 && dealers.length > 1 && dealers[1].distance !== 0) {
        log && console.log("PMAView");
        this.showFauxPMAView(dealers, selectedDealer);
      } else {
        log && console.log("SkipPMAView");
      }
    }

    if (didUpdate) {
      mapUpdates++;
    }

    log &&
      log &&
      console.log(
        "Loads:" + mapLoads,
        "Updates:" + mapUpdates,
        "MarkerRedraws:" + markerRedraws
      );
  }

  componentDidUpdate() {
    prevStoreState = JSON.parse(JSON.stringify(this.props.findDealerStore));
  }

  componentDidMount() {
    const { fetchDealers, dealers } = this.props.findDealerStore;
    if (dealers.length === 0) {
      fetchDealers(false);
      this.loadMap();
    }

    prevStoreState = JSON.parse(JSON.stringify(this.props.findDealerStore));
    window["pushDigitalData"]({
      event: "_pageLoaded",
    });
  }

  fastAreDealersSame(oldDealers: any[], newDealers: any[]): boolean {
    let isSame: boolean = true;
    if (oldDealers.length && newDealers.length) {
      if (oldDealers[0].id !== newDealers[0].id) {
        isSame = false;
      }

      if (
        oldDealers[oldDealers.length - 1].id !==
        newDealers[newDealers.length - 1].id
      ) {
        isSame = false;
      }
    }

    return isSame;
  }

  mapFixOnBreakpointChange(breakpoint: string) {
    const mapRef = this.refs.map;
    const node = ReactDOM.findDOMNode(mapRef);

    if (breakpoint === "xs" || breakpoint === "sm") {
      node.setAttribute("style", "overflow:hidden;position:relative;");
    } else {
      node.setAttribute("style", "overflow:hidden;");
    }
  }

  hasSingleMotorSportDepartment(dealer) {
    const motorSportDept = dealer.dealerDetails.filter(
      (dept) => dept.departmentName === "Motorsport Parts"
    );
    return dealer.dealerDetails.length === 1 && motorSportDept[0];
  }

  clearMarkers() {
    this.markerClusterer
      ? this.markerClusterer.removeMarkers(this.markers)
      : this.markers.forEach((marker) => marker.setMap(null));
    this.markers = [];
    this.markerClusterer = null;
  }

  createMarkerIcon(selected: boolean): any {
    return {
      url: `${selected ? pinActiveIcon : pinIcon}`,
      size: new google.maps.Size(46, 70), // This marker is 22 pixels wide by 40 pixels high.
      scaledSize: new google.maps.Size(46, 70),
      origin: new google.maps.Point(0, 0), // The origin for this image is (0, 0).
      anchor: new google.maps.Point(23, 67), // The anchor for this image is the base of the cross (11, 52).
    };
  }

  createMarkers(props: Props = this.props, createCluster: boolean = true) {
    markerRedraws++;
    const { dealers, selectedDealer, departmentsFilter, suburb, selectDealer } =
      props.findDealerStore;

    this.clearMarkers();
    let department;

    dealers.forEach((dealer: Dealer) => {
      const hasSingleMotorSportDepartment =
        this.hasSingleMotorSportDepartment(dealer);
      const department =
        departmentsFilter === "all"
          ? dealer.dealerDetails
          : dealer.dealerDetails.filter(
              (item) => item.departmentName === departmentsFilter
            );

      const mainCtaText = classNames({
        "View full details": !hasSingleMotorSportDepartment,
        "Visit website": hasSingleMotorSportDepartment,
      });

      const { geoLatitude, geoLongitude, url } =
        department.length > 0 && department[0];
      let lat = parseFloat(geoLatitude) || -38.1437848;
      let lng = parseFloat(geoLongitude) || 145.1249061;

      const content = `<h1>${dealer.name}</h1><a id="mainCTA_${
        dealer.uniqueName
      }" target="${hasSingleMotorSportDepartment ? "_blank" : "_self"}" href="${
        !hasSingleMotorSportDepartment
          ? `/dealers/${dealer.uniqueName}`
          : `${url}`
      }">${mainCtaText}</a>`;

      // const title = !!suburb ? `${dealer.name}, ${Math.round(dealer.distance)}km away` : `${dealer.name}`;
      const title = dealer.name;

      const marker = new google.maps.Marker({
        position: { lat, lng },
        map: this.map,
        title,
        icon: this.createMarkerIcon(
          !!selectedDealer && dealer.name === selectedDealer.name
        ),
        optimized: false,
      });

      marker.set("dealer", dealer);
      marker.setMap(this.map);
      this.markers.push(marker);

      google.maps.event.addListener(
        marker,
        "click",
        (function (map, marker, content, infowindow, resetMarkersIcon) {
          return function () {
            window["pushDigitalData"](
              {
                event_id: "manual_map_interaction_bluepin_clicked",
                event_value: dealer.uniqueName,
                event: "sd-click",
              },
              true
            );
            // GTM-only synthetic pageview — same click as above, so not
            // duplicated into the CDP
            TagManager.dataLayer({
              dataLayer: {
                event_id: "selected_dealer_pageview",
                event_value: dealer.uniqueName,
                event: "sd-click",
              },
            });
            selectDealer(dealer);
            resetMarkersIcon(dealer);
            map.panTo(marker.getPosition());

            infowindow.setContent(content);
            infowindow.open(map, marker);
          };
        })(this.map, marker, content, this.infowindow, this.resetMarkersIcon)
      );
    });

    if (selectedDealer && department && department.length) {
      const dealerInfo: any = selectedDealer.dealerDetails.filter(
        (item) => item.departmentName === department[0].departmentName
      );

      if (dealerInfo.length) {
        const center = new google.maps.LatLng(
          dealerInfo[0].geoLatitude,
          dealerInfo[0].geoLongitude
        );
        this.map.setCenter(center);
      }
    }

    if (createCluster) {
      this.markerClusterer = new MarkerClusterer(this.map, this.markers, {
        styles: clusterStyles,
      });
      google.maps.event.addListener(
        this.markerClusterer,
        "clusterclick",
        (cluster) => {
          window["pushDigitalData"](
            {
              event_id: "manual_map_interaction_circle_clicked",
              event: "sd-click",
            },
            true
          );
        }
      );
    }
  }

  resetMarkersIcon(selectedDealer = null) {
    this.markers.forEach((marker) => {
      const currentIcon: any = marker.getIcon();
      if (selectedDealer && marker.get("dealer").name === selectedDealer.name) {
        if (currentIcon.url !== pinActiveIcon) {
          marker.setIcon(this.createMarkerIcon(true));
        }
      } else {
        if (currentIcon.url !== pinIcon) {
          marker.setIcon(this.createMarkerIcon(false));
        }
      }
    });
  }

  loadMap() {
    mapLoads++;

    const { selectedDealer } = this.props.findDealerStore;
    const department = !!selectedDealer && selectedDealer.dealerDetails;
    const { geoLatitude, geoLongitude } =
      department.length > 0 && department[0];

    const mapRef = this.refs.map;
    const node = ReactDOM.findDOMNode(mapRef);
    let lat = parseFloat(geoLatitude) || -26.7677105;
    let lng = parseFloat(geoLongitude) || 136.1240992;

    this.map = new google.maps.Map(node, {
      center: new google.maps.LatLng(lat, lng),
      zoom: 4,
      mapTypeControl: false,
      fullscreenControl: false,
    });
    this.map.mapTypes.set(
      "styled_map",
      new google.maps.StyledMapType(mapStyles, { name: "Styled Map" })
    );
    this.map.setMapTypeId("styled_map");

    google.maps.event.addListener(this.map, "dragend", () => {
      window["pushDigitalData"](
        {
          event_id: "manual_map_interaction_dragged",
          event: "sd-drag",
        },
        true
      );
    });
    google.maps.event.addListener(this.map, "zoom_changed", () => {
      window["pushDigitalData"](
        {
          event_id: "manual_map_interaction_zoom_changed",
          event: "sd-zoom",
        },
        true
      );
    });
  }

  fetchRating(googlePlaceId) {
    const { findDealerStore, showRating } = this.props;
    const cache = dealerRatingsCache[googlePlaceId] || false;

    // if (!showRating && googlePlaceId !== "ChIJ0UPzU_Vn1moRKJpEn9wkL0Q") {
    if (!showRating) {
      findDealerStore.setRating(null);
      return;
    }

    if (cache) {
      findDealerStore.setRating(cache);
      return;
    }

    if (!!googlePlaceId) {
      const service = new google.maps.places.PlacesService(this.map);
      service.getDetails(
        {
          placeId: googlePlaceId,
        },
        function (place, status) {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            findDealerStore.setRating(place.rating);
            dealerRatingsCache[googlePlaceId] = place.rating;
          } else {
            findDealerStore.setRating(null);
            dealerRatingsCache[googlePlaceId] = null;
          }
        }.bind(this)
      );
    } else {
      findDealerStore.setRating(null);
    }
  }

  showAll() {
    let bounds: google.maps.LatLngBounds = null;
    const dealers = this.props.findDealerStore.dealers;
    const selectedDealer = this.props.findDealerStore.selectedDealer;
    const maxItems = dealers.length >= 4 ? 4 : dealers.length;

    for (let i = 0; i < maxItems; i++) {
      const details = dealers[i].dealerDetails[0];
      const latlng = new google.maps.LatLng(
        parseFloat(details.geoLatitude),
        parseFloat(details.geoLongitude)
      );
      if (bounds === null) {
        bounds = new google.maps.LatLngBounds(latlng, latlng);
      }
      bounds.extend(latlng);
    }

    this.markers.forEach((marker) => {
      if (selectedDealer && marker.get("dealer").name === selectedDealer.name) {
        this.map.setCenter(marker.getPosition());
      }
    });
    this.map.fitBounds(bounds);
  }

  showFauxPMAView(dealers, selectedDealer) {
    const selectedPos = {
      lat: parseFloat(selectedDealer.dealerDetails[0].geoLatitude),
      lng: parseFloat(selectedDealer.dealerDetails[0].geoLongitude),
    };
    let bounds: google.maps.LatLngBounds = new google.maps.LatLngBounds(
      selectedPos
    );
    let inRange: any[] = dealers.filter((dealer) => dealer.distance <= 100);
    const minDealers: number = 5;
    const maxDealers: number = 5;

    if (inRange.length < minDealers) {
      inRange = dealers.slice(0, minDealers);
    }

    if (inRange.length > maxDealers) {
      inRange = inRange.slice(0, maxDealers);
    }

    inRange.forEach((dealer) => {
      log && console.log(dealer.name, dealer.distance);
      const details = dealer.dealerDetails[0];
      const latlng = new google.maps.LatLng(
        parseFloat(details.geoLatitude),
        parseFloat(details.geoLongitude)
      );
      bounds.extend(latlng);
    });

    this.map.fitBounds(bounds);
    this.map.setCenter(selectedPos);

    this.markerClusterer.resetViewport();
    this.createMarkers(this.props, false);
    this.markerClusterer = null;

    google.maps.event.addListenerOnce(this.map, "zoom_changed", () => {
      this.createMarkers(this.props, true);
    });

    // Ensure all are on screen and selected in center
    // let zoom = false;
    // inRange.forEach(dealer => {
    //   const details = dealer.dealerDetails[0];
    //   const latlng = new google.maps.LatLng(parseFloat(details.geoLatitude), parseFloat(details.geoLongitude));
    //   google.maps.event.addListenerOnce(this.map, "bounds_changed", () => {
    //     if (this.map.getBounds().contains(latlng) === false) {
    //       this.map.setZoom(this.map.getZoom() - 1);
    //     }
    //   });
    // });
  }

  show(): void {
    this.setState({ isVisible: true });
  }

  close(isCancelled: boolean = true): void {
    this.setState({ isVisible: false });
  }

  expandDealerHours(): void {

    const dealerHours = document.querySelector(".td-dealer-hours");
    const isWeekday = document.querySelector(".td-is-weekday");

    if (dealerHours){
      if (dealerHours.classList.contains("td-hours-expanded")){
        dealerHours.classList.remove("td-hours-expanded")
      } else {
        dealerHours.classList.add("td-hours-expanded")
      }
    }

    if (isWeekday){
      if (isWeekday.classList.contains("td-weekdays-expanded")){
        isWeekday.classList.remove("td-weekdays-expanded")
      } else {
        isWeekday.classList.add("td-weekdays-expanded")
      }
    }
  }

  

  displayDealerHours = (tradingDetails) => {
    if (!tradingDetails) return "";

    const formatTime = (time) => {
        if (time === null || time === undefined || time === "") return "";
        if (String(time).toUpperCase() === "CLOSED") return "Closed";
        const [hour, minute] = String(time).split(":").map(Number);
        if (isNaN(hour) || isNaN(minute)) return "";
        const period = hour >= 12 ? "pm" : "am";
        const formattedHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        return `${formattedHour}:${minute.toString().padStart(2, "0")}${period}`;
    };

    // Determine today's hours
    const today = new Date().getDay();
    let todayHours;
    if (today >= 1 && today <= 5) {
        todayHours = `${formatTime(tradingDetails.monFriOpen)} - ${formatTime(tradingDetails.monFriClose)}`;
    } else if (today === 6) {
        todayHours = `${formatTime(tradingDetails.satOpen)} - ${formatTime(tradingDetails.satClose)}`;
    } else {
        todayHours = "Closed";
    }

    const content = `
        <p>Open today: ${todayHours}</p>
        <p class="td-is-weekday">
        <span class="weekday">Mon-Fri: ${formatTime(tradingDetails.monFriOpen)} - ${formatTime(tradingDetails.monFriClose)}</span><br>
        <span class="sat">Sat: ${formatTime(tradingDetails.satOpen)} - ${formatTime(tradingDetails.satClose)}</span><br>
        <span class="sun">Sun: ${tradingDetails.sunOpen === "CLOSED" ? "Closed" : `${formatTime(tradingDetails.sunOpen)} - ${formatTime(tradingDetails.sunClose)}</span>`}
        </p>
    `;
    
    return content;
};



onSuburbSelected(suburb: SuburbModel, isRestored: boolean): void {
  const evt: CustomEvent = new CustomEvent("locationSelected", {
    detail: { suburb: suburb, isRestored },
  });
  window.dispatchEvent(evt);
};

onSuburbPreSelected(suburb: SuburbModel): void {
  
  this.setState({ preSelectedSuburb: suburb });
  const evt: CustomEvent = new CustomEvent("locationPreSelected", {
    detail: { suburb: suburb },
  });
  window.dispatchEvent(evt);

};

handleSelectDealerMobile(): void {
  const postCodeInput = document.getElementById("post-code") as HTMLInputElement;

  if (postCodeInput && postCodeInput.classList.contains("pc__input") && postCodeInput.value.trim() !== "") {
    console.log("Input has a value and contains the class 'pc__input'");
    this.postcodeField.formSubmit();
  }
  this.close();
}



  render() {
    const { isVisible } = this.state;
    const { findDealerStore } = this.props;
    const { isFetching, suburb, dealers, selectedDealer, rating, selectDealer } =
      findDealerStore;
    const isLoading = isFetching;
    const dealerDetailsClass = classNames({
      "dealer-details": true,
      "dealer-is-selected": selectedDealer,
      "is-loading": isFetching,
    });

    let tradingDetails: DealerDetails;

    const renderDetails = () => {
      const dealer: Dealer = selectedDealer || dealers[0];

      if (!selectedDealer && dealer) {
        const evt = new CustomEvent("dealerSelected", { detail: { dealer } });
        window.dispatchEvent(evt);
      }

      // Prefer the "Vehicle Sales" department, but fall back to the first
      // available department. A searched dealer without an exact "Vehicle Sales"
      // entry (or with none at all) must not throw here: there is no error
      // boundary, so a render throw unmounts the whole retailer section and the
      // dealer info / change controls disappear until a full page reload.
      tradingDetails =
        dealer && dealer.dealerDetails
          ? dealer.dealerDetails.find(
              (details) => details.departmentName === "Vehicle Sales"
            ) || dealer.dealerDetails[0]
          : undefined;

      if (!dealer || !tradingDetails) {
        return null;
      }

      const directionsUrl = `https://www.google.com/maps/dir/Current+Location/${tradingDetails.address}+${tradingDetails.postCode}`;
      return (
        <div>
              <div className="td-dealer-info">

                <p className="td-dealer-postcode-change">Based on your postcode <span className="td-dealer-postcode-value">{suburb?.postcode || "2000"}</span> (<a className="custom-change" onClick={(e) => {
                    e.preventDefault();
                    findDealerStore.showPostcodeOverlayActive();
                  }}>change</a>)</p>

                  <div className="td-dealer-columns">
                    <div className="td-dealer-left-col">
                      <p className="td-dealer-name">{dealer.name}</p>
                      <div className="td-dealer-address">
                        {tradingDetails.address}<br/>
                        {`${tradingDetails.suburb} ${dealer.state} ${tradingDetails.postCode}`}<br/>
                        <span className="td-dealer-direction is-collapsible">
                          <a className="direction_cta" href={directionsUrl} target="_blank">Get Directions</a>
                          <div className="pipe"></div>
                          <a className="viewmap_cta" onClick={() => this.show()} >view map</a>
                        </span>
                      </div>
                    </div>

                    <div className="td-dealer-right-col">
                      <p className="td-dealer-phone"><a href={`tel:${tradingDetails.phone}`}>{tradingDetails.phone}</a></p>
                      <p className="td-dealer-url"><a href={`${tradingDetails.url}`} target="_blank">{tradingDetails.url}</a></p>
                      <div className="td-dealer-hours is-collapsible" onClick={() => this.expandDealerHours()}
                        dangerouslySetInnerHTML={{ __html: this.displayDealerHours(tradingDetails) }}
                      />
                    </div>
                  </div>

              </div>
        </div>
      )
    }

    return (
      <div className="find-dealer-widget">
        <div className="find-dealer-widget__body">
        <Loader isLoading={isFetching} isPositionRelative={true} />
        {!isFetching && dealers.length > 0 && (
           renderDetails()
        )}
        {!isFetching && dealers.length === 0 && (
          <p className="no-results-found">
            We couldn't find a retailer with the selected department in your area.{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                findDealerStore?.showPostcodeOverlayActive();
              }}
            >
              Change your location
            </a>{" "}
            or select another department.
          </p>
        )}
          <section className={`pc ${isVisible ? "pc__visible" : ""}`}>
        <div className="pc__overlay" onClick={() => this.close()} />
        <div className="pc__content pc__map">
          <div className="pc__close" onClick={() => this.close()} />
          <h4 className="pc__title"></h4>
          <div className="pc__copy" />
          <div className="pc__form">
          <div className="dealer-location">
          <div className="dealer-postcode-header">
            Please select a retailer
          </div>
          <div className="dealer-postcode-field">
          <PostcodeField
                ref={(instance) => {
                  this.postcodeField = instance;
                }}
                placeholder="Enter postcode"
                onSelected={(suburb, isRestored) => this.onSuburbSelected(suburb, isRestored)}
                onPreSelect={(suburb) => {
                  this.onSuburbPreSelected(suburb);
                }}
              />
              </div>
            <div className="dealers-map" ref="map" />
            {!!selectedDealer && (
              <div className="container align-btn-helper">
                <button
                  className="btn-show-all-dealers"
                  onClick={(e) => {
                    e.preventDefault();
                    this.showAll();
                  }}
                >
                  Show surrounding Retailers
                </button>
              </div>
            )}
            <div className="container">
              <div className={dealerDetailsClass}>
                <div className="dealer-details__inner">
                  <h2 className="txt-xl">Find a Retailer</h2>
                  {!isFetching && dealers.length > 0 && (
                    <RetailerInfo rating={rating} />
                  )}
                  {!isFetching && dealers.length === 0 && (
                    <p className="no-results-found">
                      We couldn't find a retailer with the selected department
                      in your area.
                      <br />
                      <br />
                      Please{" "}
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          findDealerStore.showPostcodeOverlayActive();
                        }}
                      >
                        change your location
                      </a>{" "}
                      or select another department.
                    </p>
                  )}
                  {!isFetching && (
                    <div className="dealer-postcode">
                      <div>
                        {!!selectedDealer ? (
                          <FilterDepartmentsDropDown />
                        ) : (
                          <FilterDepartmentsList />
                        )}
                        {dealers.length !== 0 && !selectedDealer && (
                          <p className="no-dealer-selected">
                            <strong>
                              Enter your postcode to find your nearest retailer
                            </strong>
                            <a
                              href="#"
                              title="Click here to change the postcode."
                              className="btn"
                              onClick={(e) => {
                                e.preventDefault();
                                findDealerStore.showPostcodeOverlayActive();
                              }}
                            >
                              Enter postcode
                            </a>
                          </p>
                        )}
                        {!!suburb && !isFetching && selectedDealer && (
                          <p>
                            {`Location: `}
                            <strong>{`${suburb.name}, ${suburb.postcode} `}</strong>
                            <a
                              href="#"
                              title="Click here to change the postcode."
                              onClick={(e) => {
                                e.preventDefault();
                                findDealerStore.showPostcodeOverlayActive();
                              }}
                            >
                              edit
                            </a>
                          </p>
                        )}
                        {selectedDealer && !suburb && (
                          <p>
                            See retailers in your area:{" "}
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                findDealerStore.showPostcodeOverlayActive();
                              }}
                            >
                              <span className="cta">Set location</span>
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="dealer-postcode-field">
                  <p className="text-center">
                      <button id="selectDealerBtnMap" className="btn btn--primary" onClick={()=> { this.handleSelectDealerMobile() }}>Select preferred retailer</button>
                  </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
        </section>
        </div>
      </div>
    );
  }
}

export default RetailersPopupMap;
function find(dealerDetails: any, arg1: (details: any) => boolean): DealerDetails {
  throw new Error("Function not implemented.");
}

